import React, { useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, authApi } from '../lib/api';
import {
  User, Sun, Moon, Type, Globe, Heart, MessageCircle,
  Shield, Info, ExternalLink, ChevronRight, LogOut, LogIn, UserPlus, Lock, Edit3,
  Camera, X, MessageSquare, ZoomIn, ZoomOut, Check, Move
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import AdminPanel from '../components/AdminPanel';
import AboutSection from '../components/AboutSection';
import PrivacyPolicy from '../components/PrivacyPolicy';

export default function ProfilePage() {
  const { t, language, setLanguage, languages } = useLanguage();
  const { theme, toggleTheme, fontSize, setFontSize } = useTheme();
  const { user, isAuthenticated, login, register, logout, updateProfile } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminInput, setShowAdminInput] = useState(false);
  
  // Auth state
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [authUsername, setAuthUsername] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  
  // Edit profile
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [editPseudonym, setEditPseudonym] = useState('');
  
  // Profile photo
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [showFullscreenPhoto, setShowFullscreenPhoto] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  
  // Message settings
  const [acceptMessages, setAcceptMessages] = useState(true);

  // Password change
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [changePwdLoading, setChangePwdLoading] = useState(false);

  // Account deletion
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) return;
    setChangePwdLoading(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      toast.success(t('password_changed') || 'Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('password_change_failed') || 'Failed to change password');
    } finally {
      setChangePwdLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) return;
    try {
      await authApi.deleteAccount(deletePassword);
      toast.success(t('account_deleted') || 'Account deleted');
      logout();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('account_delete_failed') || 'Failed to delete account');
    }
  };

  const fontSizes = [
    { value: 's', label: t('small') },
    { value: 'm', label: t('medium') },
    { value: 'l', label: t('large') },
    { value: 'xl', label: t('extra_large') },
  ];

  const handleAuth = async () => {
    if (!authUsername || !authPassword) return;
    setAuthLoading(true);
    try {
      if (authMode === 'login') {
        await login(authUsername, authPassword);
        toast.success(t('login_success') || 'Logged in successfully!');
      } else {
        await register(authUsername, authPassword);
        toast.success(t('register_success') || 'Account created!');
      }
      setShowAuth(false);
      setAuthUsername('');
      setAuthPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || (t('auth_failed') || 'Authentication failed'));
    } finally {
      setAuthLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      const updates = {};
      if (editPseudonym) updates.pseudonym = editPseudonym;
      if (Object.keys(updates).length > 0) {
        await updateProfile(updates);
        toast.success(t('profile_updated') || 'Profile updated!');
        setShowEditProfile(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (t('update_failed') || 'Update failed'));
    }
  };

  const handleAdminVerify = async () => {
    try {
      const res = await adminApi.verifyPassword(adminPassword);
      if (res.data.valid) {
        setShowAdmin(true);
        setShowAdminInput(false);
        toast.success(t('admin_access_granted') || 'Admin access granted');
      }
    } catch (err) {
      toast.error(t('invalid_password') || 'Invalid password');
    }
  };

  // Photo cropping state
  const [showPhotoCrop, setShowPhotoCrop] = useState(false);
  const [rawPhoto, setRawPhoto] = useState(null);
  const [photoPosition, setPhotoPosition] = useState({ x: 0, y: 0 });
  const [photoScale, setPhotoScale] = useState(1);
  const [isFromCamera, setIsFromCamera] = useState(false);
  
  // Handle profile photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('image_too_large') || 'Image must be less than 2MB');
      return;
    }
    
    // Check if from camera (input has capture attribute)
    const fromCamera = e.target.hasAttribute('capture');
    setIsFromCamera(fromCamera);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result;
      // Show crop dialog
      setRawPhoto(base64);
      setPhotoPosition({ x: 0, y: 0 });
      setPhotoScale(1);
      setShowPhotoCrop(true);
      setShowPhotoOptions(false);
    };
    reader.readAsDataURL(file);
    
    // Reset input value to allow re-selecting same file
    e.target.value = '';
  };
  
  // Process and save the cropped photo
  const handleSavePhoto = async () => {
    if (!rawPhoto) return;
    
    try {
      // Create canvas to process image
      const img = new Image();
      img.src = rawPhoto;
      
      await new Promise((resolve) => {
        img.onload = resolve;
      });
      
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 256; // Output size
      canvas.width = size;
      canvas.height = size;
      
      // Calculate crop dimensions
      const minDim = Math.min(img.width, img.height);
      const scale = photoScale;
      const cropSize = minDim / scale;
      
      // Center crop position with user adjustment
      const offsetX = (img.width - cropSize) / 2 + (photoPosition.x * cropSize / 100);
      const offsetY = (img.height - cropSize) / 2 + (photoPosition.y * cropSize / 100);
      
      // If from front camera, flip horizontally
      if (isFromCamera) {
        ctx.translate(size, 0);
        ctx.scale(-1, 1);
      }
      
      // Draw cropped and scaled image
      ctx.drawImage(
        img,
        Math.max(0, Math.min(offsetX, img.width - cropSize)),
        Math.max(0, Math.min(offsetY, img.height - cropSize)),
        cropSize,
        cropSize,
        0,
        0,
        size,
        size
      );
      
      // Convert to base64
      const processedBase64 = canvas.toDataURL('image/jpeg', 0.85);
      
      // Save to backend
      await authApi.updateSettings({ profile_photo: processedBase64 });
      setProfilePhoto(processedBase64);
      setShowPhotoCrop(false);
      setRawPhoto(null);
      toast.success(t('profile_photo') + ' updated!');
    } catch (err) {
      toast.error(t('failed_update_photo') || 'Failed to update photo');
    }
  };

  // Handle remove photo
  const handleRemovePhoto = async () => {
    try {
      await authApi.updateSettings({ profile_photo: '' });
      setProfilePhoto(null);
      toast.success(t('photo_removed') || 'Photo removed');
    } catch (err) {
      toast.error(t('failed_remove_photo') || 'Failed to remove photo');
    }
  };

  // Handle accept messages toggle
  const handleToggleMessages = async () => {
    try {
      const newValue = !acceptMessages;
      await authApi.updateSettings({ accept_messages: newValue });
      setAcceptMessages(newValue);
      toast.success(newValue ? (t('messages_enabled') || 'Messages enabled') : (t('messages_disabled_toast') || 'Messages disabled'));
    } catch (err) {
      toast.error(t('failed_update_settings') || 'Failed to update settings');
    }
  };

  // Load user settings on mount
  React.useEffect(() => {
    if (user) {
      setProfilePhoto(user.profile_photo || null);
      setAcceptMessages(user.accept_messages !== false);
    }
  }, [user]);

  // Close photo options when clicking outside
  React.useEffect(() => {
    if (showPhotoOptions) {
      const handleClickOutside = () => setShowPhotoOptions(false);
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showPhotoOptions]);

  if (showAdmin) {
    return <AdminPanel password={adminPassword} onBack={() => setShowAdmin(false)} />;
  }

  if (showAbout) {
    return <AboutSection onBack={() => setShowAbout(false)} />;
  }

  if (showPrivacy) {
    return <PrivacyPolicy onBack={() => setShowPrivacy(false)} />;
  }

  return (
    <div className="px-4 pt-4 pb-4">
      <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2 mb-6">
        <User className="w-5 h-5 text-primary" />
        {t('profile')}
      </h1>

      <div className="space-y-3">
        {/* Fullscreen Photo Modal */}
        <AnimatePresence>
          {showFullscreenPhoto && (profilePhoto || user?.profile_photo) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
              onClick={() => setShowFullscreenPhoto(false)}
            >
              <button
                onClick={() => setShowFullscreenPhoto(false)}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                data-testid="close-fullscreen-profile-photo"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              <motion.img
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                src={profilePhoto || user?.profile_photo}
                alt={user?.pseudonym || 'Profile'}
                className="max-w-full max-h-[80vh] rounded-xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <p className="absolute bottom-8 text-white text-lg font-medium">
                {user?.pseudonym || 'Anonymous'}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Photo Crop Modal */}
        <AnimatePresence>
          {showPhotoCrop && rawPhoto && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
            >
              {/* Header */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
                <button
                  onClick={() => { setShowPhotoCrop(false); setRawPhoto(null); }}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
                <span className="text-white font-medium">{t('adjust_photo') || 'Adjust Photo'}</span>
                <button
                  onClick={handleSavePhoto}
                  className="p-2 rounded-full bg-primary hover:bg-primary/90 transition-colors"
                  data-testid="save-cropped-photo"
                >
                  <Check className="w-5 h-5 text-primary-foreground" />
                </button>
              </div>

              {/* Preview */}
              <div className="relative w-64 h-64 rounded-full overflow-hidden border-4 border-white/30">
                <img
                  src={rawPhoto}
                  alt="Preview"
                  className="absolute w-full h-full object-cover"
                  style={{
                    transform: `scale(${photoScale}) translate(${-photoPosition.x}%, ${-photoPosition.y}%) ${isFromCamera ? 'scaleX(-1)' : ''}`,
                    transformOrigin: 'center center',
                  }}
                />
              </div>

              {/* Controls */}
              <div className="mt-8 space-y-4 w-full max-w-xs">
                {/* Zoom */}
                <div className="flex items-center gap-3">
                  <ZoomOut className="w-4 h-4 text-white/70" />
                  <input
                    type="range"
                    min="1"
                    max="3"
                    step="0.1"
                    value={photoScale}
                    onChange={(e) => setPhotoScale(parseFloat(e.target.value))}
                    className="flex-1 h-2 bg-white/20 rounded-lg appearance-none cursor-pointer accent-primary"
                  />
                  <ZoomIn className="w-4 h-4 text-white/70" />
                </div>

                {/* Position hint */}
                <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
                  <Move className="w-3 h-3" />
                  <span>{t('drag_to_position') || 'Use arrows to position'}</span>
                </div>

                {/* Position controls */}
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setPhotoPosition(p => ({ ...p, x: Math.max(-50, p.x - 5) }))}
                    className="p-2 rounded bg-white/10 hover:bg-white/20 text-white text-sm"
                  >
                    ←
                  </button>
                  <button
                    onClick={() => setPhotoPosition(p => ({ ...p, y: Math.max(-50, p.y - 5) }))}
                    className="p-2 rounded bg-white/10 hover:bg-white/20 text-white text-sm"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => setPhotoPosition(p => ({ ...p, y: Math.min(50, p.y + 5) }))}
                    className="p-2 rounded bg-white/10 hover:bg-white/20 text-white text-sm"
                  >
                    ↓
                  </button>
                  <button
                    onClick={() => setPhotoPosition(p => ({ ...p, x: Math.min(50, p.x + 5) }))}
                    className="p-2 rounded bg-white/10 hover:bg-white/20 text-white text-sm"
                  >
                    →
                  </button>
                  <button
                    onClick={() => setPhotoPosition({ x: 0, y: 0 })}
                    className="p-2 rounded bg-white/10 hover:bg-white/20 text-white text-xs ml-2"
                  >
                    {t('center') || 'Center'}
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* User Card */}
        {isAuthenticated ? (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3 mb-3">
              {/* Profile Photo */}
              <div className="relative">
                <button 
                  className={`w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden ${(profilePhoto || user?.profile_photo) ? 'cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all' : ''}`}
                  onClick={() => (profilePhoto || user?.profile_photo) ? setShowFullscreenPhoto(true) : fileInputRef.current?.click()}
                >
                  {profilePhoto || user?.profile_photo ? (
                    <img 
                      src={profilePhoto || user?.profile_photo} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-xl font-medium text-primary">
                      {(user?.pseudonym || 'A').charAt(0).toUpperCase()}
                    </span>
                  )}
                </button>
                <button 
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md"
                  onClick={(e) => { e.stopPropagation(); setShowPhotoOptions(!showPhotoOptions); }}
                >
                  <Camera className="w-3 h-3" />
                </button>
                {/* Photo source options popup */}
                <AnimatePresence>
                  {showPhotoOptions && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="absolute top-full left-0 mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          cameraInputRef.current?.click();
                          setShowPhotoOptions(false);
                        }}
                        className="w-full px-4 py-2.5 text-xs text-left hover:bg-accent flex items-center gap-2"
                        data-testid="photo-from-camera"
                      >
                        <Camera className="w-4 h-4" />
                        {t('take_photo') || 'Take Photo'}
                      </button>
                      <button
                        onClick={() => {
                          fileInputRef.current?.click();
                          setShowPhotoOptions(false);
                        }}
                        className="w-full px-4 py-2.5 text-xs text-left hover:bg-accent flex items-center gap-2 border-t border-border"
                        data-testid="photo-from-gallery"
                      >
                        <User className="w-4 h-4" />
                        {t('choose_from_gallery') || 'Choose from Gallery'}
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="user"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold" data-testid="profile-pseudonym">{user?.pseudonym}</p>
                <p className="text-xs text-muted-foreground">@{user?.username}</p>
                {(profilePhoto || user?.profile_photo) && (
                  <button
                    onClick={handleRemovePhoto}
                    className="text-[10px] text-destructive/70 hover:text-destructive mt-1"
                  >
                    {t('remove_photo')}
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setEditPseudonym(user?.pseudonym || '');
                  setShowEditProfile(!showEditProfile);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors duration-200"
                data-testid="profile-edit-button"
              >
                <Edit3 className="w-3.5 h-3.5" />
                {t('edit') || 'Edit'}
              </button>
              <button
                onClick={logout}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-destructive/30 text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors duration-200"
                data-testid="profile-logout-button"
              >
                <LogOut className="w-3.5 h-3.5" />
                {t('logout') || 'Logout'}
              </button>
            </div>
            
            {/* Edit profile form */}
            <AnimatePresence>
              {showEditProfile && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 space-y-2"
                >
                  <input
                    type="text"
                    value={editPseudonym}
                    onChange={(e) => setEditPseudonym(e.target.value)}
                    placeholder="Pseudonym"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
                    data-testid="profile-edit-pseudonym"
                  />
                  <button
                    onClick={handleUpdateProfile}
                    className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors duration-200"
                    data-testid="profile-save-button"
                  >
                    {t('save_changes') || 'Save Changes'}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Change Password */}
            <div className="mt-3">
              <button
                onClick={() => setShowChangePassword(!showChangePassword)}
                className="w-full flex items-center justify-between py-2 text-xs text-muted-foreground hover:text-foreground"
                data-testid="profile-change-password-toggle"
              >
                <span className="flex items-center gap-1.5"><Lock className="w-3 h-3" />{t('change_password') || 'Change Password'}</span>
                <ChevronRight className={`w-3 h-3 transition-transform ${showChangePassword ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {showChangePassword && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2 mt-2">
                    <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} placeholder={t('current_password') || 'Current password'}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]" data-testid="current-password-input" />
                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder={t('new_password') || 'New password (min 6 chars)'}
                      className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]" data-testid="new-password-input" />
                    <button onClick={handleChangePassword} disabled={changePwdLoading || !currentPassword || !newPassword}
                      className="w-full py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50" data-testid="change-password-submit">
                      {changePwdLoading ? '...' : (t('change_password') || 'Change Password')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Delete Account */}
            <div className="mt-1">
              <button
                onClick={() => setShowDeleteAccount(!showDeleteAccount)}
                className="w-full flex items-center justify-between py-2 text-xs text-destructive/70 hover:text-destructive"
                data-testid="profile-delete-account-toggle"
              >
                <span className="flex items-center gap-1.5"><X className="w-3 h-3" />{t('delete_account') || 'Delete Account'}</span>
                <ChevronRight className={`w-3 h-3 transition-transform ${showDeleteAccount ? 'rotate-90' : ''}`} />
              </button>
              <AnimatePresence>
                {showDeleteAccount && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-2 mt-2">
                    <p className="text-[10px] text-destructive">{t('delete_account_warning') || 'This will permanently delete your account, messages, gems, and all data. This action cannot be undone.'}</p>
                    <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} placeholder={t('confirm_password') || 'Enter password to confirm'}
                      className="w-full px-3 py-2 rounded-lg border border-destructive/30 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-destructive/50" data-testid="delete-account-password" />
                    <button onClick={handleDeleteAccount} disabled={!deletePassword}
                      className="w-full py-2 rounded-lg bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50" data-testid="delete-account-submit">
                      {t('confirm_delete_account') || 'Permanently Delete Account'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <User className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm font-semibold">{t('guest_mode') || 'Guest Mode'}</p>
                <p className="text-xs text-muted-foreground">{t('sign_in_prompt') || 'Sign in to personalize your experience'}</p>
              </div>
            </div>
            
            {!showAuth ? (
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowAuth(true); setAuthMode('login'); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors duration-200"
                  data-testid="auth-login-button"
                >
                  <LogIn className="w-3.5 h-3.5" />
                  {t('login') || 'Login'}
                </button>
                <button
                  onClick={() => { setShowAuth(true); setAuthMode('register'); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors duration-200"
                  data-testid="auth-register-button"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  {t('register') || 'Register'}
                </button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-2"
              >
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setAuthMode('login')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      authMode === 'login' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {t('login') || 'Login'}
                  </button>
                  <button
                    onClick={() => setAuthMode('register')}
                    className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      authMode === 'register' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {t('register') || 'Register'}
                  </button>
                </div>
                
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="text"
                    value={authUsername}
                    onChange={(e) => setAuthUsername(e.target.value)}
                    placeholder={t('username') || 'Username'}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
                    data-testid="auth-username-input"
                    autoComplete="username"
                  />
                </div>
                
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <input
                    type="password"
                    value={authPassword}
                    onChange={(e) => setAuthPassword(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                    placeholder={t('password') || 'Password'}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
                    data-testid="auth-password-input"
                    autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
                  />
                </div>

                {authMode === 'register' && (
                  <p className="text-[10px] text-muted-foreground text-center">
                    {t('username_hint') || 'Choose a unique username (3-20 characters)'}
                  </p>
                )}
                
                <button
                  onClick={handleAuth}
                  disabled={authLoading || !authUsername || !authPassword}
                  className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors duration-200"
                  data-testid="auth-submit-button"
                >
                  {authLoading ? t('loading') : (authMode === 'login' ? t('login') || 'Login' : t('create_account') || 'Create Account')}
                </button>
                
                <button
                  onClick={() => setShowAuth(false)}
                  className="w-full py-1.5 text-xs text-muted-foreground hover:text-foreground"
                >
                  {t('cancel')}
                </button>
              </motion.div>
            )}
          </div>
        )}

        {/* Language */}
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="flex items-center gap-3 mb-3">
            <Globe className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('language')}</span>
          </label>
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
            data-testid="settings-language-select"
          >
            {languages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName} ({lang.name})
              </option>
            ))}
          </select>
        </div>

        {/* Theme */}
        <div className="rounded-xl border border-border bg-card p-4">
          <button
            onClick={toggleTheme}
            className="w-full flex items-center justify-between"
            data-testid="settings-theme-switch"
          >
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon className="w-4 h-4 text-muted-foreground" />
              ) : (
                <Sun className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">{t('theme')}</span>
            </div>
            <div className={`w-11 h-6 rounded-full transition-colors duration-200 relative ${
              theme === 'dark' ? 'bg-primary' : 'bg-muted'
            }`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                theme === 'dark' ? 'translate-x-[22px]' : 'translate-x-0.5'
              }`} />
            </div>
          </button>
        </div>

        {/* Font Size */}
        <div className="rounded-xl border border-border bg-card p-4">
          <label className="flex items-center gap-3 mb-3">
            <Type className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('font_size')}</span>
          </label>
          <div className="grid grid-cols-4 gap-2" data-testid="settings-font-size">
            {fontSizes.map((fs) => (
              <button
                key={fs.value}
                onClick={() => setFontSize(fs.value)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors duration-200 ${
                  fontSize === fs.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-accent'
                }`}
              >
                {fs.label}
              </button>
            ))}
          </div>
        </div>

        {/* Accept Messages - Only for authenticated users */}
        {isAuthenticated && (
          <div className="rounded-xl border border-border bg-card p-4">
            <button
              onClick={handleToggleMessages}
              className="w-full flex items-center justify-between"
              data-testid="settings-accept-messages"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{t('accept_messages')}</span>
              </div>
              <div className={`w-11 h-6 rounded-full transition-colors duration-200 relative ${
                acceptMessages ? 'bg-primary' : 'bg-muted'
              }`}>
                <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                  acceptMessages ? 'translate-x-[22px]' : 'translate-x-0.5'
                }`} />
              </div>
            </button>
          </div>
        )}

        {/* Donate */}
        <a
          href="https://www.paypal.com/donate?business=yohannelias@yahoo.fr"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors duration-200"
          data-testid="settings-donate-button"
        >
          <div className="flex items-center gap-3">
            <Heart className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('donate')}</span>
          </div>
          <ExternalLink className="w-4 h-4 text-muted-foreground" />
        </a>

        {/* Suggest Modification */}
        <a
          href="mailto:yohannelias@yahoo.fr?subject=Spiritual%20Gems%20-%20Suggestion"
          className="flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors duration-200"
          data-testid="settings-suggest-button"
        >
          <div className="flex items-center gap-3">
            <MessageCircle className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('suggest')}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </a>

        {/* About */}
        <button
          onClick={() => setShowAbout(true)}
          className="w-full flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors duration-200"
          data-testid="settings-about-button"
        >
          <div className="flex items-center gap-3">
            <Info className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('about')}</span>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Privacy Policy & Security */}
        <button
          onClick={() => setShowPrivacy(true)}
          className="w-full flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:bg-accent/30 transition-colors duration-200"
          data-testid="settings-privacy-button"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-4 h-4 text-green-500" />
            <div>
              <span className="text-sm font-medium">{t('privacy_policy') || 'Privacy & Security'}</span>
              <span className="text-[10px] text-green-500 ml-2 bg-green-500/10 px-1.5 py-0.5 rounded">
                {t('encrypted') || 'Encrypted'}
              </span>
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </button>

        {/* Admin */}
        <div className="rounded-xl border border-border bg-card p-4">
          <button
            onClick={() => setShowAdminInput(!showAdminInput)}
            className="w-full flex items-center justify-between"
            data-testid="settings-admin-button"
          >
            <div className="flex items-center gap-3">
              <Shield className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">{t('admin')}</span>
            </div>
            <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${showAdminInput ? 'rotate-90' : ''}`} />
          </button>
          <AnimatePresence>
            {showAdminInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 flex gap-2"
              >
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAdminVerify()}
                  placeholder={t('admin_password')}
                  className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--focus))]"
                  data-testid="admin-password-input"
                />
                <button
                  onClick={handleAdminVerify}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors duration-200"
                  data-testid="admin-password-submit"
                >
                  OK
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
